import { useState, useEffect, useCallback } from "react";
import { Button, Flex, List, Text } from "@theorgchartdev/storybook";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { appstr } from "@coreapp/utils/appstr";
import { CreateScimToken, DeleteScimToken, ListScimTokens } from "@/Utils/Promises";
import * as StateLib from "@/Utils/StateLib";
import { Globals } from "@coreapp/CoreApp/Globals";
import { kAdminUser } from "@coreapp/constants";

const _fmtDate = iso => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (_e) {
    return iso;
  }
};

export const ScimTokenPanel = () => {
  const [tokens, setTokens] = useState([]);
  const [newToken, setNewToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const result = await ListScimTokens();
      if (result && result.success) {
        setTokens(result.tokens || []);
      } else {
        setError(result && result.message ? result.message : appstr("SCIMTokenListError"));
      }
    } catch (e) {
      setError(appstr("SCIMTokenListError"));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onCreate = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await CreateScimToken();
      if (result && result.success) {
        setNewToken(result.token);
        await refresh();
      } else {
        setError(result && result.message ? result.message : appstr("SCIMTokenCreateError"));
      }
    } catch (e) {
      setError(appstr("SCIMTokenCreateError"));
    }
    setBusy(false);
  };

  const onDelete = async tokenId => {
    setBusy(true);
    setError(null);
    try {
      const result = await DeleteScimToken(tokenId);
      if (result && result.success) {
        await refresh();
      } else {
        setError(result && result.message ? result.message : appstr("SCIMTokenDeleteError"));
      }
    } catch (e) {
      setError(appstr("SCIMTokenDeleteError"));
    }
    setBusy(false);
  };

  const onCopy = () => {
    if (newToken && navigator.clipboard) {
      navigator.clipboard.writeText(newToken).catch(() => {});
    }
  };

  const onDismissNewToken = () => setNewToken(null);

  const wsMgr = StateLib.GetWSMgr();
  const currentUser = wsMgr?.GetUserName?.();
  const currentEmail = wsMgr?.GetUserEmail?.();
  const isAdmin = Globals._accountInfo?.GetUserRole?.() === kAdminUser;
  const equalStringLowerCase = (left, right) =>
    left && right && String(left).toLowerCase() === String(right).toLowerCase();
  const canDeleteToken = token =>
    isAdmin ||
    equalStringLowerCase(token.created_by, currentUser) ||
    equalStringLowerCase(token.created_by, currentEmail);

  const tokenItems = tokens.map(t => {
    const tokenContent = (
      <Flex direction="column" gap="2px" overflow="hidden">
        <Text size="small">{`${appstr("SCIMTokenCreatedBy")}: ${t.created_by || "—"}`}</Text>
        <Text size="xsmall" colorModifier="grey">{`${appstr("SCIMTokenCreatedAt")}: ${_fmtDate(
          t.created_at
        )} · ${appstr("SCIMTokenLastUsed")}: ${_fmtDate(t.last_used_at)}`}</Text>
      </Flex>
    );
    const actions = canDeleteToken(t)
      ? [
          {
            iconName: "trash-can",
            iconProps: { color: "error" },
            onClick: () => onDelete(t.id),
            disabled: busy,
            dataTestId: "account-settings-authorization-scim-token-delete-btn"
          }
        ]
      : [];

    return {
      content: <ActionListItem contentBefore={tokenContent} actions={actions} visible="always" />,
      divider: false,
      value: t.id
    };
  });

  return (
    <div className="oc-scim-token-panel">
      <div className="oc-scim-token-panel__intro">{appstr("SCIMTokenPanelIntro")}</div>

      {newToken && (
        <div className="oc-scim-token-panel__new-token">
          <div className="oc-scim-token-panel__new-token-title">{appstr("SCIMTokenNewTitle")}</div>
          <div className="oc-scim-token-panel__new-token-warning">
            {appstr("SCIMTokenNewWarning")}
          </div>
          <code className="oc-scim-token-panel__new-token-value">{newToken}</code>
          <div className="oc-scim-token-panel__new-token-actions">
            <Button
              modifier="secondary"
              onClick={onCopy}
              dataTestId="account-settings-authorization-scim-copy-btn"
            >
              {appstr("SCIMTokenCopy")}
            </Button>
            <Button
              modifier="link"
              onClick={onDismissNewToken}
              dataTestId="account-settings-authorization-scim-dismiss-btn"
            >
              {appstr("SCIMTokenDismiss")}
            </Button>
          </div>
        </div>
      )}

      {error && <div className="oc-scim-token-panel__error">{error}</div>}

      {tokenItems.length > 0 ? (
        <List
          enableVirtualization={false}
          options={tokenItems}
          listWidth="fill"
          optionSize="medium"
          showCheck={false}
          value={null}
        />
      ) : (
        <div className="oc-scim-token-panel__empty">{appstr("SCIMTokenNoneActive")}</div>
      )}

      <div className="oc-scim-token-panel__actions">
        <Button
          modifier="primary"
          iconProps={{ name: "plus" }}
          onClick={onCreate}
          disabled={busy}
          dataTestId="account-settings-authorization-scim-generate-btn"
        >
          {appstr("SCIMTokenGenerate")}
        </Button>
      </div>
    </div>
  );
};
